import React, { useEffect, useState } from 'react';
import { Box, Button, CircularProgress, Tooltip, Typography } from '@mui/material';
import { Refresh as RefreshIcon, WarningAmber as WarningIcon } from '@mui/icons-material';
import { fmtInt } from '../../OnlineJournal/format';
import { RemainsMeta } from '../remainsApi';
import { freshnessOf } from '../remainsCalc';

interface Props {
  meta: RemainsMeta;
  refreshing: boolean;
  onRefresh: () => void;
}

const WARN = '#b45309';
const textSx = { fontSize: '0.78rem', color: '#64748b' };

/** Свежесть данных 1С: когда обновлялись, сколько аптек загружено, идёт ли загрузка сейчас */
const DataFreshness: React.FC<Props> = ({ meta, refreshing, onRefresh }) => {
  const [now, setNow] = useState(() => Date.now());
  // «N мин назад» обновляется без новых запросов
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 60000);
    return () => window.clearInterval(timer);
  }, []);

  const stats = meta.data;
  const fresh = freshnessOf(stats, now);
  const loadingNow = Boolean(stats && stats.isLoading);
  const errors = stats ? Number(stats.errorPharmacies) || 0 : 0;

  let body: React.ReactNode;
  if (meta.dataError || !stats) {
    body = (
      <Tooltip title={meta.dataError || ''} disableHoverListener={!meta.dataError}>
        <Typography sx={{ ...textSx, color: WARN }}>Состояние данных 1С недоступно</Typography>
      </Tooltip>
    );
  } else {
    body = (
      <Typography sx={textSx}>
        {fresh.updated
          ? <>Данные 1С обновлены <Box component="b" sx={{ color: fresh.stale ? WARN : '#334155' }}>{fresh.updated}</Box> ({fresh.ageLabel})</>
          : 'Данные 1С ещё не загружались'}
        {fresh.updated && <> · аптек: {fmtInt(Number(stats.successPharmacies) || 0)}</>}
        {errors > 0 && <Box component="span" sx={{ color: WARN }}>, с ошибкой: {fmtInt(errors)}</Box>}
        {stats.status === 'error' && <Box component="span" sx={{ color: '#b91c1c' }}> · последнее обновление завершилось с ошибкой</Box>}
      </Typography>
    );
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: 'wrap', px: 0.5, minHeight: 30 }}>
      {fresh.stale && fresh.updated && !loadingNow && (
        <Tooltip title="Обычно остатки из 1С обновляются каждые 30 минут. Количество в аптеках могло измениться">
          <WarningIcon sx={{ fontSize: 18, color: WARN }} />
        </Tooltip>
      )}
      {body}
      {loadingNow && stats && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <CircularProgress size={14} />
          <Typography sx={{ ...textSx, color: '#2563eb' }}>
            Идёт загрузка из 1С{stats.progress ? `: ${fmtInt(stats.progress.done)} из ${fmtInt(stats.progress.total)} аптек` : ''}
          </Typography>
        </Box>
      )}
      <Box sx={{ flexGrow: 1 }} />
      {meta.permissions.refresh && (
        <Tooltip title="Запустить загрузку остатков из 1С вне расписания. Занимает несколько минут, поиск в это время работает по прежним данным">
          <span>
            <Button
              size="small" onClick={onRefresh} disabled={refreshing || loadingNow}
              startIcon={refreshing ? <CircularProgress size={14} color="inherit" /> : <RefreshIcon />}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              Обновить из 1С
            </Button>
          </span>
        </Tooltip>
      )}
    </Box>
  );
};

export default React.memo(DataFreshness);
